// OrgChartCard — one absolutely-positioned unit card on the chart canvas.
// Only the geometry (left/top/width/height) is inline; everything else is BEM.

import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common';
import { useLevelConfig } from '@/api/endpoints/levelsApi';
import { LayoutRect } from './orgChartLayout';

interface OrgChartCardProps {
  rec: LayoutRect;
  selected: boolean;
  onSelect: (id: string) => void;
}

const OrgChartCard = ({ rec, selected, onSelect }: OrgChartCardProps) => {
  const { t } = useTranslation();
  const unit = rec.node;
  const level = useLevelConfig()(unit.level);
  const isInactive = unit.status === 'inactive';

  const cardClass = [
    'org-chart__card',
    `org-chart__card--${level.tone}`,
    unit.level === 0 && 'org-chart__card--root',
    selected && 'is-selected',
    isInactive && 'is-inactive',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={cardClass}
      style={{ left: rec.x, top: rec.y, width: rec.w, height: rec.h }}
      role='button'
      tabIndex={0}
      aria-selected={selected}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={() => onSelect(unit.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(unit.id);
        }
      }}
    >
      <div className='org-chart__card-head'>
        <span className={`org-chart__tile org-chart__tile--${level.tone}`}>
          <Icon name={level.icon} size={20} />
        </span>
        <span className='org-chart__card-label'>
          <span className='org-chart__card-title'>{unit.name}</span>
          <span className='org-chart__card-sub'>
            {level.name}
            <span className='org-chart__card-code'>{`ND-${unit.referenceNumber}`}</span>
          </span>
        </span>
        {isInactive && (
          <span className='org-chart__status-dot' title={t('structure_status_inactive')} />
        )}
      </div>

      <div className='org-chart__card-body'>
        {unit.memberCount > 0 ? (
          <>
            <Icon name='users' size={17} />
            <span className='org-chart__card-count'>
              {unit.memberCount === 1
                ? t('structure_person')
                : t('structure_people', { count: unit.memberCount })}
            </span>
            <Icon name='chevron-right' size={16} className='org-chart__card-chevron' />
          </>
        ) : (
          <span className='org-chart__card-empty'>
            <Icon name='user' size={14} />
            {t('structure_chart_no_members')}
          </span>
        )}
      </div>

      {unit.tag && (
        <div className='org-chart__card-tags'>
          <span className='org-chart__tag'>
            <Icon name='tag' size={11} />
            <span className='org-chart__tag-text'>{unit.tag}</span>
          </span>
        </div>
      )}
    </div>
  );
};

export default OrgChartCard;
